<template>
  <div class="mb-0.5 flex flex-col">
    <label
      v-if="label !== ''"
      class="flex items-center justify-between text-neutral-800"
      :class="[
        size === 'small' ? 'mb-px text-sm' : '',
        size === 'medium' ? 'mb-1 text-base' : '',
        size === 'large' ? 'mb-2 text-lg' : '',
        font === 'normal' ? 'flex items-center justify-between text-neutral-800' : '',
        font === 'bold' ? 'flex items-center justify-between font-extrabold text-neutral-800' : ''
      ]"
      :for="labelId || undefined"
    >
      {{ label }}
      <span
        v-if="optional"
        :class="[
          size === 'small' ? 'text-xs' : '',
          size === 'medium' ? 'text-sm' : '',
          size === 'large' ? 'text-base' : '',
          font === 'normal' ? 'text-neutral-300' : '',
          font === 'bold' ? 'text-neutral-500' : ''
        ]"
        >{{ t('optional') }}</span
      >

      <span v-if="tooltip" class="-m-3 cursor-help p-3 text-orange-400">
        <InfuseTooltip :content="tooltip" offset-distance="8">
          <font-awesome-icon :icon="['fal', 'info-circle']" />
        </InfuseTooltip>
      </span>

      <slot name="label" :size="size" />
    </label>

    <div v-if="direction === 'row'" class="mb-4 flex h-full flex-row items-center">
      <slot />
    </div>
    <slot v-else />

    <div
      :class="[
        size === 'small' ? 'mt-0.5 text-xs' : '',
        size === 'medium' ? 'mt-1 mb-0.5 text-sm' : '',
        size === 'large' ? 'mt-1.5 mb-1 text-base' : '',
        font === 'normal' ? 'flex flex-wrap items-center justify-between text-neutral-400' : '',
        font === 'bold' ? 'flex flex-wrap items-center justify-between text-neutral-600' : ''
      ]"
    >
      <div v-if="!!unsafeHelpText" class="basis-full" v-html="unsafeHelpText"></div>
      <div v-if="!!helpText" class="basis-full">{{ helpText }}</div>
      <div v-if="!!error" class="basis-full text-red-500">{{ error }}</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { provide, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { FieldLabelIdInjectionKey } from './InfuseFieldInjection';
import InfuseTooltip from './InfuseTooltip.vue';

interface Props {
  direction?: 'row' | 'column';
  label?: string;
  size?: 'small' | 'medium' | 'large';
  font?: 'normal' | 'bold';
  helpText?: string;
  unsafeHelpText?: string; // never use with user content
  error?: string;
  optional?: boolean;
  tooltip?: string;
}

const props = withDefaults(defineProps<Props>(), {
  direction: 'column',
  label: '',
  size: 'medium',
  font: 'normal',
  helpText: '',
  unsafeHelpText: '',
  error: undefined,
  optional: false,
  tooltip: undefined
});

const { t } = useI18n();

const labelId = ref<string | null>(null);

provide(FieldLabelIdInjectionKey, labelId);

watch(
  () => props.label,
  (label) => {
    if (label) {
      labelId.value = label.toLowerCase().replace(/\s/g, '-');
    } else {
      labelId.value = null;
    }
  },
  { immediate: true }
);
</script>

<i18n>
{
  "en": {
    "optional": "optional"
  },
  "nl": {
    "optional": "optioneel"
  }
}
</i18n>
